import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { CollectedAt, Pill, label } from './components/primitives'
import { getGaps, listSubjects, type Gaps, type Subject } from './api'

/**
 * What is missing — the screen that tells an analyst what to go and find.
 *
 * The plan singles this out as the one most likely to be cut for time, and says not to cut
 * it. The reason is that every other screen here shows what somebody already collected; a
 * dossier product without this one quietly optimises for looking complete rather than being
 * complete.
 */
export function GapsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<{ subject: Subject; gaps: Gaps }[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const subjects = await listSubjects()
      const gaps = await Promise.all(subjects.map(async (subject) => ({
        subject, gaps: await getGaps(subject.id),
      })))
      // Emptiest first: the point of the screen is what to do next, not a register.
      setRows(gaps.sort((a, b) =>
        b.gaps.emptyCategoryCodes.length - a.gaps.emptyCategoryCodes.length))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Working out what is missing" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Gaps"
        description="Empty categories, stale attributes and when anything was last collected —
                     ordered by how much is missing."
      />
      <Panel>
        <ErpTable head={<>
          <Th>Subject</Th>
          <Th className="text-right">Items</Th>
          <Th className="text-right">Empty categories</Th>
          <Th className="text-right axis-hide-sm">Stale attributes</Th>
          <Th>Last collected</Th>
          <Th className="axis-hide-sm">What is missing</Th>
        </>}>
          {rows.length === 0 && <EmptyRow colSpan={6} message="No files open yet." />}
          {rows.map(({ subject, gaps }) => (
            <tr key={subject.id} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/dossier/${subject.id}`)}>
              <Td><span className="font-medium">{subject.displayName}</span></Td>
              <Td className="text-right tabular-nums">{subject.itemCount}</Td>
              <Td className="text-right tabular-nums">{gaps.emptyCategoryCodes.length}</Td>
              <Td className="text-right tabular-nums axis-hide-sm">{gaps.staleAttributeCount}</Td>
              <Td><CollectedAt at={gaps.lastCollectedAt} /></Td>
              <Td className="axis-hide-sm">
                <div className="flex flex-wrap gap-0.5">
                  {gaps.emptyCategoryCodes.slice(0, 6).map((c) => (
                    <Pill key={c} className="border-border text-muted-foreground">
                      {label(c)}
                    </Pill>
                  ))}
                  {gaps.emptyCategoryCodes.length > 6 && (
                    <span className="text-[10px] text-muted-foreground">
                      +{gaps.emptyCategoryCodes.length - 6} more
                    </span>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
